import { useEffect, useState } from "react";
import { ListGroup, ListGroupItem } from "reactstrap";
import Button from "../../common/Button";
import KaModal from "../../common/KaModal";
import SpinnerButton from "../../common/SpinnerButton";
import { adminFacingErrorMessage } from "../../common/userFacingMessages/userFacingMessages";
import { useGetAvailableBaysForStagedPurchaseOrder } from "../../requests/useGetAvailableBaysForStagedPurchaseOrder";
import { StagedPurchaseOrderDetails } from "../../requests/useGetStagedPurchaseOrders";
import { useReassignStagedPurchaseOrderBay } from "../../requests/useReassignStagedPurchaseOrderBay";

interface Props {
  stagedPurchaseOrder: StagedPurchaseOrderDetails;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onClose: () => void;
}

const ReassignBayStagedPurchaseOrderModal = ({ stagedPurchaseOrder, onSuccess, onError, onClose }: Props) => {
  const [selectedBayId, setSelectedBayId] = useState("");

  const availableBays = useGetAvailableBaysForStagedPurchaseOrder(stagedPurchaseOrder.id);

  useEffect(() => {
    if (availableBays.data) {
      const currentBay = availableBays.data.find(b => b.name === stagedPurchaseOrder.bay);
      if (currentBay) setSelectedBayId(currentBay.id);
    }
  }, [availableBays.data, stagedPurchaseOrder.bay]);

  const currentBayId = availableBays.data?.find(b => b.name === stagedPurchaseOrder.bay)?.id;

  const reassignBay = useReassignStagedPurchaseOrderBay(stagedPurchaseOrder.id, selectedBayId, {
    onSuccess: () => {
      onSuccess(`Bay reassigned for purchase order ${stagedPurchaseOrder.purchaseOrderNumber}`);
      onClose();
    },
    onError: (err) => {
      onError(adminFacingErrorMessage(err));
      onClose();
    },
  });

  return (
    <KaModal
      isOpen
      toggle={onClose}
      title={`Reassign Bay — Purchase Order ${stagedPurchaseOrder.purchaseOrderNumber}`}
      body={
        !availableBays.data
          ? <div className="d-flex justify-content-center py-3">
              <span className="spinner-border text-primary" role="status" />
            </div>
          : <ListGroup>
              {[...availableBays.data].sort((a, b) => {
                const rank = (bay: typeof a) => {
                  if (bay.id === currentBayId) return 0;
                  if (bay.isCompatible && !bay.isTemporarilyDisabled) return 1;
                  return 2;
                };
                const rankDiff = rank(a) - rank(b);
                if (rankDiff !== 0) return rankDiff;
                return a.name.localeCompare(b.name);
              }).map(bay => (
                <ListGroupItem
                  key={bay.id}
                  tag="button"
                  action
                  active={selectedBayId === bay.id}
                  disabled={!bay.isCompatible}
                  className="d-flex justify-content-between align-items-center"
                  onClick={() => setSelectedBayId(bay.id)}>
                  {bay.name}
                  <div className="d-flex gap-2">
                    {bay.id === currentBayId && <span className="badge badge-ka-blue">Current</span>}
                    {bay.isTemporarilyDisabled && <span className="badge bg-danger">Disabled</span>}
                    {!bay.isCompatible && !bay.isTemporarilyDisabled && <span className="badge bg-secondary">Incompatible</span>}
                  </div>
                </ListGroupItem>
              ))}
            </ListGroup>
      }
      footer={
        <div className="container">
          <div className="row justify-content-between">
            <Button className="col-3 btn-secondary" onClick={onClose} disabled={reassignBay.isLoading}>
              Cancel
            </Button>
            <SpinnerButton
              className="col-3 btn-primary"
              disabled={!selectedBayId || selectedBayId === currentBayId}
              spinning={reassignBay.isLoading}
              onClick={() => reassignBay.request({})}>
              Reassign
            </SpinnerButton>
          </div>
        </div>
      }
    />
  );
}

export default ReassignBayStagedPurchaseOrderModal;
